/**
 * Purchase-event property derivation (platform-agnostic).
 *
 * These are the pure resolvers behind `subscription_purchase_initiated` /
 * `_completed` / `_failed`. They previously lived inline at the mobile call
 * site (`ChoosePlan.js`) and in a mobile-local `utils/analyticsHelpers.js`
 * copy, which is how four separate telemetry defects shipped unnoticed:
 *
 *   1. `parseFloat(priceString.replace(/[^0-9.-]+/g, ''))` turned the
 *      comma-decimal locales into 100x their real value ("13,99 €" -> 1399).
 *      124/132 EUR and 23/23 PLN purchases over 90d were inflated.
 *   2. plan-type matching missed quarterly/semiannual entirely, so ~200
 *      purchases reported plan_type 'unknown'.
 *   3. `introPrice !== null` is TRUE when introPrice is `undefined`, so
 *      priced purchases were flagged is_trial.
 *   4. `is_trial` was asked at purchase-*initiation* time, when whether the
 *      user actually lands on a trial is not yet knowable.
 *
 * Nothing here imports RevenueCat/StoreKit/Stripe — callers pass already-read
 * plain objects, so every branch is reachable from a golden test.
 *
 * FORWARD-ONLY: this changes what new events report. Historical PostHog rows
 * keep their wrong values and are never rewritten; split dashboards on the
 * release date instead.
 */

/**
 * Plan-type matchers, most specific first.
 *
 * ORDER IS LOAD-BEARING: 'semiannual'.includes('annual') === true, and
 * '3month'/'sixmonth'.includes('month') === true. A shorter term must never
 * be tested before a longer term that contains it, or `cissp_semiannual_1499`
 * silently reports 'annual'.
 *
 * DELIBERATELY NARROW: there is no bare 'month'/'year' needle. A bare
 * substring would resolve `newyear_promo` to 'annual' and `12month` to
 * 'monthly', and no live identifier needs it — every shipped sku spells out
 * monthly/quarterly/semiannual/annual. Reporting 'unknown' for an
 * unrecognised period is honest; guessing it wrong is not. Add the explicit
 * spelling here when a new sku shape appears.
 */
const PLAN_TYPE_MATCHERS = [
  ['lifetime', ['lifetime', 'forever', 'onetime', 'one_time']],
  ['semiannual', ['semiannual', 'semi_annual', 'semi-annual', 'sixmonth', 'six_month', '6month', '6_month']],
  ['quarterly', ['quarterly', 'quarter', 'threemonth', 'three_month', '3month', '3_month']],
  ['annual', ['annual', 'yearly', 'twelvemonth', 'twelve_month', '12month', '12_month']],
  ['monthly', ['monthly']],
  ['weekly', ['weekly']],
];

/**
 * Map a store product / package identifier to a canonical billing period.
 *
 * Matches on substrings because the identifiers are not consistently shaped
 * across stores and brands (`cissp_monthly_499`, `android_cissp_quarterly`,
 * `com.learnzapp.comptia.sub.monthly`).
 *
 * @param {string} productId
 * @returns {'monthly'|'quarterly'|'semiannual'|'annual'|'lifetime'|'weekly'|'unknown'}
 */
export const getSubscriptionPlanType = (productId) => {
  if (typeof productId !== 'string' || !productId) return 'unknown';
  const id = productId.toLowerCase();
  for (const [planType, needles] of PLAN_TYPE_MATCHERS) {
    if (needles.some((needle) => id.includes(needle))) return planType;
  }
  return 'unknown';
};

/**
 * Store/platform period labels -> the canonical vocabulary above.
 *
 * The two platforms name the same billing period three different ways:
 * RevenueCat `packageType` ('THREE_MONTH'), the web product `period`
 * ('Semi-Annual'), and the product identifier ('cissp_quarterly_999'). Unless
 * they all collapse to one vocabulary, `plans_shown` and `plan_type` cannot be
 * joined in a funnel — which is exactly why the hardcoded
 * `['monthly','annual','lifetime']` went unnoticed for so long.
 */
const PERIOD_ALIASES = {
  weekly: 'weekly', week: 'weekly',
  monthly: 'monthly', month: 'monthly', one_month: 'monthly',
  quarterly: 'quarterly', quarter: 'quarterly', three_month: 'quarterly', threemonth: 'quarterly',
  semiannual: 'semiannual', semi_annual: 'semiannual', six_month: 'semiannual', sixmonth: 'semiannual',
  annual: 'annual', annually: 'annual', yearly: 'annual', year: 'annual', twelve_month: 'annual',
  lifetime: 'lifetime',
};

/** Canonical ordering by duration, so `plans_shown` is stable and comparable. */
const PERIOD_ORDER = ['weekly', 'monthly', 'quarterly', 'semiannual', 'annual', 'lifetime'];

/** Billing months per canonical period. null where months are not meaningful. */
const PERIOD_MONTHS = {
  weekly: null,
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
  lifetime: null,
};

/**
 * Billing months for a canonical period.
 *
 * Used to reconcile a period against month-count config (mobile's
 * `APP_DATA.SUBSCRIPTION_PLANS` is a list of month counts, e.g. [1, 3, 6, 12]),
 * and by the per-month rate display.
 *
 * @param {string} period canonical period, or any label toCanonicalPeriod accepts
 * @returns {number|null}
 */
export const getPeriodMonths = (period) => {
  const canonical = toCanonicalPeriod(period);
  return PERIOD_MONTHS[canonical] ?? null;
};

/**
 * Normalize any platform's period label to the canonical vocabulary.
 *
 * @param {string} input e.g. 'THREE_MONTH' (RevenueCat), 'Semi-Annual' (web)
 * @returns {string} canonical period, or 'unknown'
 */
export const toCanonicalPeriod = (input) => {
  if (typeof input !== 'string' || !input) return 'unknown';
  const key = input.trim().toLowerCase().replace(/[\s-]+/g, '_');
  return PERIOD_ALIASES[key] || PERIOD_ALIASES[key.replace(/_/g, '')] || 'unknown';
};

/**
 * Build the `plans_shown` property from whatever the paywall actually offers.
 *
 * Deduped, duration-ordered, 'unknown' dropped. Never hardcode this: the value
 * it replaces claimed a `lifetime` tier that does not exist and omitted both
 * quarterly and semiannual.
 *
 * @param {Array<string>} labels period labels in any platform's dialect
 * @returns {Array<string>} canonical periods, duration-ordered
 */
export const getPlansShown = (labels) => {
  if (!Array.isArray(labels)) return [];
  const seen = new Set();
  for (const label of labels) {
    const period = toCanonicalPeriod(label);
    if (period !== 'unknown') seen.add(period);
  }
  return PERIOD_ORDER.filter((p) => seen.has(p));
};

/**
 * Parse a store-formatted, locale-aware price string into a number.
 *
 * FALLBACK ONLY — prefer the numeric price the store SDK already gives you
 * (see resolvePurchasePrice). This exists for the case where only the
 * display string survived.
 *
 * Separator disambiguation, since "1,800" and "13,99" mean different things:
 *   - both '.' and ',' present -> the LAST one is the decimal separator
 *   - exactly one separator, 1-2 trailing digits -> decimal separator
 *   - anything else (3 trailing digits, or repeated) -> thousands separator
 *
 * @param {string} priceString e.g. "$16.99", "13,99 €", "¥1,800", "99,99 zł"
 * @returns {number|null} null when unparseable — never a silent 0
 */
export const parseLocalizedPrice = (priceString) => {
  if (typeof priceString !== 'string') return null;

  // Drop currency symbols, ISO codes and every flavour of space, including
  // NBSP (U+00A0) and narrow NBSP (U+202F), both used as thousands
  // separators by fr-FR / pl-PL number formatting.
  const stripped = priceString.replace(/[^\d.,-]/g, '');

  // Abbreviated currency marks carry their own period — "19,00 kr." (da-DK,
  // nb-NO, sv-SE) and "Fr. 19.90" (de-CH). Stripping only letters leaves that
  // period stranded against the number, where it wins the separator
  // tie-break below and re-creates the exact 100x inflation this module
  // exists to kill ("19,00 kr." -> 1900). A separator with no digits on the
  // far side is never a decimal point, so drop leading/trailing ones.
  const cleaned = stripped.replace(/^[.,]+/, '').replace(/[.,]+$/, '');
  if (!cleaned) return null;

  const isNegative = cleaned.startsWith('-');
  const digits = cleaned.replace(/-/g, '');
  if (!digits || !/\d/.test(digits)) return null;

  const lastDot = digits.lastIndexOf('.');
  const lastComma = digits.lastIndexOf(',');

  let decimalSep = null;
  if (lastDot >= 0 && lastComma >= 0) {
    decimalSep = lastDot > lastComma ? '.' : ',';
  } else if (lastDot >= 0 || lastComma >= 0) {
    const sep = lastDot >= 0 ? '.' : ',';
    const idx = Math.max(lastDot, lastComma);
    const trailingDigits = digits.length - idx - 1;
    const occurrences = digits.split(sep).length - 1;
    if (occurrences === 1 && trailingDigits > 0 && trailingDigits <= 2) {
      decimalSep = sep;
    }
  }

  let normalized;
  if (decimalSep) {
    const thousandsSep = decimalSep === '.' ? ',' : '.';
    normalized = digits.split(thousandsSep).join('').replace(decimalSep, '.');
  } else {
    // No decimal separator at all: every '.'/',' present is a grouping mark.
    normalized = digits.replace(/[.,]/g, '');
  }

  const value = Number.parseFloat(normalized);
  if (!Number.isFinite(value)) return null;
  return isNegative ? -value : value;
};

/**
 * Resolve the purchase price, preferring the SDK's numeric field.
 *
 * `price_source` is emitted alongside the value so the fallback parser's
 * real-world hit rate is observable in PostHog rather than assumed.
 *
 * @param {{price?: number, priceString?: string}} product
 * @returns {{price: number|null, price_source: 'numeric'|'parsed'|'unavailable'}}
 */
export const resolvePurchasePrice = (product) => {
  const numeric = product?.price;
  if (typeof numeric === 'number' && Number.isFinite(numeric)) {
    return { price: numeric, price_source: 'numeric' };
  }
  const parsed = parseLocalizedPrice(product?.priceString);
  if (parsed !== null) return { price: parsed, price_source: 'parsed' };
  return { price: null, price_source: 'unavailable' };
};

/**
 * Whether the product advertises an introductory/trial offer.
 *
 * This is what the old `is_trial` flag was actually measuring — offer
 * availability, not what the user got. Uses loose `!=` deliberately so both
 * `null` and `undefined` count as "no offer"; the strict `!== null` it
 * replaces returned TRUE for `undefined`.
 *
 * @param {{introPrice?: unknown}} product
 * @returns {boolean}
 */
export const hasIntroOffer = (product) => product?.introPrice != null;

/**
 * Whether the *granted* entitlement is a free trial.
 *
 * Only knowable after the purchase resolves, from the store's period type
 * (RevenueCat: 'NORMAL' | 'INTRO' | 'TRIAL'). Returns null when the period
 * type is absent — unknown and false are different facts.
 *
 * @param {{periodType?: string}} entitlement
 * @returns {boolean|null}
 */
export const resolveTrialState = (entitlement) => {
  const periodType = entitlement?.periodType;
  if (typeof periodType !== 'string' || !periodType) return null;
  return periodType.toUpperCase() === 'TRIAL';
};

/**
 * Canonical purchase-lifecycle event names.
 *
 * CANCELLED is split out from FAILED (2026-07): over 90d, 74% of
 * `subscription_purchase_failed` rows were `user_cancelled: true` — users who
 * simply dismissed the App Store / Play dialog, not real failures. Lumping them
 * together inflated every "purchase failure rate" metric ~4x. Emitting a
 * distinct event lets funnels exclude deliberate backs-out without breaking the
 * historical series (the `user_cancelled` flag still separates old rows).
 *
 * FORWARD-ONLY: pre-split history keeps landing under FAILED; split dashboards
 * on the release date, or reconstruct via `user_cancelled`.
 */
export const PURCHASE_EVENTS = {
  INITIATED: 'subscription_purchase_initiated',
  COMPLETED: 'subscription_purchase_completed',
  FAILED: 'subscription_purchase_failed',
  CANCELLED: 'subscription_purchase_cancelled',
};

/** RevenueCat PurchasesErrorCode for a user-dismissed purchase dialog. */
const PURCHASE_CANCELLED_ERROR_CODE = '1';

/**
 * Decide which event a failed `purchasePackage` should emit, and the
 * `user_cancelled` flag to stamp on it.
 *
 * A user dismissing the native purchase dialog surfaces as a thrown error. That
 * is not a failure — it routes to CANCELLED. Everything else (store/network
 * errors, receipt collisions, the offerings-load crash, ...) is a genuine
 * FAILED.
 *
 * Detection accepts EITHER signal: RevenueCat has deprecated the `userCancelled`
 * boolean (now typed `boolean | null`) in favour of
 * `code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR` ('1'). Relying on the
 * boolean alone would silently revert a cancel to FAILED — re-inflating the very
 * metric this split exists to fix — on any SDK path that populates the code but
 * leaves the boolean null.
 *
 * `user_cancelled` is derived here (not read from a caller-supplied literal) so
 * the event name and the flag can never disagree.
 *
 * @param {{userCancelled?: boolean|null, code?: string|number}} error the caught purchase error
 * @returns {{event: string, user_cancelled: boolean}}
 */
export const resolvePurchaseFailureEvent = (error) => {
  const user_cancelled = !!(
    error && (error.userCancelled || String(error.code) === PURCHASE_CANCELLED_ERROR_CODE)
  );
  return {
    event: user_cancelled ? PURCHASE_EVENTS.CANCELLED : PURCHASE_EVENTS.FAILED,
    user_cancelled,
  };
};

/**
 * Build the shared property bag for subscription purchase events.
 *
 * @param {object} purchase
 * @param {string} [purchase.packageIdentifier] selected package identifier
 * @param {object} [purchase.product]           store product (price, priceString, currencyCode, introPrice)
 * @param {string} [purchase.currencyCode]      explicit override
 * @param {object} [purchase.entitlement]       granted entitlement; omit pre-purchase
 * @param {object} [additionalData]             event-specific extras, spread last
 */
export const buildPurchaseProperties = (purchase = {}, additionalData = {}) => {
  const { packageIdentifier, product, currencyCode, entitlement } = purchase;
  const identifier = packageIdentifier || product?.identifier || null;
  const { price, price_source } = resolvePurchasePrice(product);

  return {
    plan_type: getSubscriptionPlanType(identifier),
    price,
    price_source,
    currency: currencyCode || product?.currencyCode || 'USD',
    // Offer availability (known up front) vs. what the user actually got
    // (known only once the entitlement exists). Kept as separate fields so
    // neither has to lie on the event where the other is unknowable.
    intro_offer_available: hasIntroOffer(product),
    is_trial: resolveTrialState(entitlement),
    product_id: identifier,
    ...additionalData,
  };
};

/**
 * The standard paywall-event property block, identical across web + mobile so
 * per-mode / per-platform funnels join cleanly. ADDITIVE: callers spread it
 * alongside their existing event properties — do NOT rename or drop the existing
 * ones (`default_period`, `subscription_type`, `period`, and the in-app-location
 * `surface` = 'choose_plan'/'web_plans'); historical funnels depend on them.
 * Brand/app id already rides the `app_name` PostHog super property, so it is not
 * repeated here.
 *
 * The device platform is emitted as its OWN `platform` key (NOT `surface`): some
 * events already use `surface` for an in-app location, so overloading it would
 * make one property mean two things. With a distinct key the platform lands on
 * every paywall event, including `plan_selected`.
 *
 * @param {object} input
 * @param {'subscription'|'one_time'} [input.paymentMode]  mobile is always 'subscription'
 * @param {string|null} [input.planTerm]      selected term (any dialect; canonicalized)
 * @param {string|null} [input.promotedTerm]  pickDefaultPlan output (any dialect; canonicalized)
 * @param {'web'|'ios'|'ipad'|'android'|'android_tablet'} [input.platform]
 * @returns {{payment_mode: string|null, plan_term: string|null, promoted_term: string|null, platform: string|null}}
 */
export const buildPaywallEventProps = ({ paymentMode, planTerm, promotedTerm, platform } = {}) => ({
  payment_mode: paymentMode ?? null,
  plan_term: planTerm ? toCanonicalPeriod(planTerm) : null,
  promoted_term: promotedTerm ? toCanonicalPeriod(promotedTerm) : null,
  platform: platform ?? null,
});
